export const pick = (obj = {}, arr = []) => {
  return arr.reduce((acc, i) => {
    if (obj?.[i] !== undefined) {
      acc[i] = obj[i];
      return acc;
    }
  }, {});
};

export const capitalize = (str) => {
  if (!str) return "";
  const s = String(str).trim();
  if (!s) return "";
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

export const mask = (value, maskLength = 4) => {
  if (!value) return "";
  const str = String(value);
  if (maskLength <= 0) return str;
  if (maskLength >= str.length) return "*".repeat(str.length);
  return (
    "*".repeat(str.length - maskLength) + str.slice(str.length - maskLength)
  );
};

export const jsonStringify = (value) => {
  try {
    if(typeof value === "string") return value;
    return JSON.stringify(value);
  } catch (error) {
    return value;
  }
};

export const jsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

/**
 * A utility for managing sessionStorage with JSON support and error handling.
 */
export const sessionStorageUtil = {
  set(key, value) {
    if (typeof window === "undefined" || !key) return false;

    try {
      sessionStorage.setItem(key, jsonStringify(value));
      return true;
    } catch (err) {
      console.error("sessionStorage set error:", err);
      return false;
    }
  },

  get(key, fallback = null) {
    if (typeof window === "undefined" || !key) {
      return fallback;
    }

    try {
      const value = sessionStorage.getItem(key);

      if (value === null || value === undefined || value === "") {
        return fallback;
      }

      return jsonParse(value);
    } catch (err) {
      console.error("sessionStorage get error:", err);
      return fallback;
    }
  },

  remove(key) {
    if (typeof window === "undefined" || !key) return false;

    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (err) {
      console.error("sessionStorage remove error:", err);
      return false;
    }
  },

  clear() {
    if (typeof window === "undefined") return false;

    try {
      sessionStorage.clear();
      return true;
    } catch (err) {
      console.error("sessionStorage clear error:", err);
      return false;
    }
  },

  has(key) {
    if (typeof window === "undefined" || !key) return false;

    return sessionStorage.getItem(key) !== null;
  },
};

export const DateUtils = {
  toShortMonthDate: (date) => {
    if (!date) return "";
    const d = new Date(date);
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return d.toLocaleDateString("en-GB", options);
  },

  hasExpired: (expiry, equality = true) => {
    const now = Date.now();
    const expiryTime = new Date(expiry).getTime();
    return equality ? now >= expiryTime : now > expiryTime;
  },
};

/**
 * Formats a numeric string/number to the specified precision, stripping the decimal
 * portion only when all decimal digits are zero. Returns the formatted value in the
 * same type as the input, or the original value if formatting fails.
 */
export const formatNumber = (
  value,
  decimalPlaces = 2,
  stripTrailingZeros = false,
) => {
  try {
    const isString = typeof value === "string";
    const isNumber = typeof value === "number";

    if (!isString && !isNumber) {
      return value;
    }

    const num = Number(value);

    if (Number.isNaN(num) || !Number.isFinite(num)) {
      return value;
    }

    const fixed = num.toFixed(decimalPlaces);

    let formatted;

    // Remove decimal part entirely if all decimal digits are 0
    if (Number(fixed) % 1 === 0) {
      formatted = String(Math.trunc(Number(fixed)));
    } else if (stripTrailingZeros) {
      formatted = String(Number(fixed));
    } else {
      formatted = fixed;
    }

    return isNumber ? Number(formatted) : formatted;
  } catch {
    return value;
  }
};

export const isMasked = (str) => /x/i.test(str);
